// src/pages/Production/CostCalculation.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    searchContainer: {
        marginBottom: '24px'
    },
    searchInputContainer: {
        position: 'relative',
        maxWidth: '400px'
    },
    searchIcon: {
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        width: '16px',
        height: '16px'
    },
    searchInput: {
        padding: '12px 16px 12px 44px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        width: '100%',
        background: '#f8fafc',
        outline: 'none',
        transition: 'all 0.2s'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    trHoverActive: {
        background: '#f8fafc'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b'
    },
    costCard: {
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '15px'
    },
    costCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    },
    costCardTitle: {
        fontSize: '16px',
        fontWeight: '600',
        margin: 0
    },
    costItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: '1px solid #f1f5f9'
    },
    costLabel: {
        color: '#64748b'
    },
    costValue: {
        fontWeight: '500'
    },
    totalCost: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '15px 0',
        borderTop: '2px solid #e2e8f0',
        fontWeight: '700',
        fontSize: '16px'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    }
};

function CostCalculation() {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [products, setProducts] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [subcontractors, setSubcontractors] = useState([]);
    const [productionOrders, setProductionOrders] = useState([]);
    const [rawMaterialPurchases, setRawMaterialPurchases] = useState([]); // New: purchases data
    const [searchTerm, setSearchTerm] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch real data from Firebase
    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch products
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch recipes
        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => setRecipes(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch raw materials
        const unsubRawMaterials = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterials')),
            snap => setRawMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch subcontractors (suppliers with isSubcontractor flag)
        const unsubSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'suppliers')),
            snap => setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch production orders for cost history
        const unsubProductionOrders = onSnapshot(
            query(collection(db, 'companies', companyId, 'productionOrders')),
            snap => setProductionOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // NEW: Fetch raw material purchases
        const unsubPurchases = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterialPurchases')),
            snap => {
                setRawMaterialPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => {
            unsubProducts();
            unsubRecipes();
            unsubRawMaterials();
            unsubSubcontractors();
            unsubProductionOrders();
            unsubPurchases();
        };
    }, [userData]);

    // Helper function to get latest purchase price for a material
    const getMaterialPurchasePrice = (materialId, materialName) => {
        // Find all purchases containing this material
        const purchasesWithMaterial = rawMaterialPurchases
            .filter(p => p.lineItems && p.lineItems.some(item =>
                item.productId === materialId || item.name === materialName || item.productName === materialName
            ))
            .sort((a, b) => {
                const dateA = a.purchaseDate?.toDate?.() || a.purchaseDate?.seconds ? new Date(a.purchaseDate.seconds * 1000) : new Date(0);
                const dateB = b.purchaseDate?.toDate?.() || b.purchaseDate?.seconds ? new Date(b.purchaseDate.seconds * 1000) : new Date(0);
                return dateB - dateA; // Sort by date descending (most recent first)
            });

        if (purchasesWithMaterial.length > 0) {
            // Get the most recent purchase
            const latestPurchase = purchasesWithMaterial[0];
            const lineItem = latestPurchase.lineItems.find(item =>
                item.productId === materialId || item.name === materialName || item.productName === materialName
            );
            if (lineItem && lineItem.price > 0) {
                return {
                    price: lineItem.price,
                    date: latestPurchase.purchaseDate,
                    supplierName: latestPurchase.supplierName
                };
            }
        }
        return null;
    };

    // Calculate costs for each product based on recipes and raw materials
    const productsWithCosts = useMemo(() => {
        // Combine dedicated raw materials with product raw materials
        // Use purchase prices when available
        const allMaterials = [
            ...rawMaterials.map(rm => {
                const purchaseInfo = getMaterialPurchasePrice(rm.id, rm.name);
                return {
                    id: rm.id,
                    name: rm.name,
                    unitPrice: purchaseInfo?.price || rm.unitPrice || 0,
                    unit: rm.unit || 'Adet',
                    hasPurchasePrice: !!purchaseInfo,
                    lastPurchaseDate: purchaseInfo?.date,
                    lastSupplier: purchaseInfo?.supplierName
                };
            }),
            ...products.filter(p => p.isRawMaterial).map(p => {
                const purchaseInfo = getMaterialPurchasePrice(p.id, p.name);
                return {
                    id: p.id,
                    name: p.name,
                    unitPrice: purchaseInfo?.price || p.purchasePrice || p.salePrice || 0,
                    unit: p.unit || 'Adet',
                    hasPurchasePrice: !!purchaseInfo,
                    lastPurchaseDate: purchaseInfo?.date,
                    lastSupplier: purchaseInfo?.supplierName
                };
            })
        ];

        return products
            .filter(p => !p.isRawMaterial) // Exclude raw materials from the list
            .map(product => {
                // Find recipe for this product
                const recipe = recipes.find(r =>
                    r.product === product.name ||
                    r.productId === product.id
                );

                let rawMaterialCost = 0;
                let subcontractingCost = 0;
                let laborCost = 0;
                let overheadCost = 0;

                if (recipe && recipe.stages) {
                    recipe.stages.forEach(stage => {
                        // Calculate raw material costs from input materials
                        if (stage.inputMaterials) {
                            stage.inputMaterials.forEach(material => {
                                const rawMat = allMaterials.find(rm =>
                                    rm.name === material.material ||
                                    rm.id === material.materialId
                                );
                                if (rawMat) {
                                    rawMaterialCost += (rawMat.unitPrice || 0) * (material.quantity || 0);
                                }
                            });
                        }

                        // Calculate subcontracting costs
                        if (stage.type === 'subcontracting' && stage.subcontractors) {
                            stage.subcontractors.forEach(sub => {
                                const supplier = subcontractors.find(s => s.id === sub.supplierId);
                                if (supplier && supplier.costTariff) {
                                    // Find matching tariff
                                    const tariff = supplier.costTariff.find(t =>
                                        t.unit === (stage.outputProduct?.unit || 'Adet')
                                    ) || supplier.costTariff[0];

                                    if (tariff) {
                                        const quantity = stage.outputProduct?.quantity || 1;
                                        if (tariff.type === 'per_unit') {
                                            subcontractingCost += (tariff.value || 0) * quantity;
                                        } else if (tariff.type === 'per_kg') {
                                            subcontractingCost += (tariff.value || 0) * (stage.outputProduct?.weight || 1);
                                        } else {
                                            subcontractingCost += tariff.value || 0;
                                        }
                                    }
                                }
                                // Add labor costs from subcontractor
                                laborCost += sub.laborCost || 0;
                            });
                        }

                        // Calculate internal labor costs (from duration)
                        if (stage.type === 'internal' && stage.duration) {
                            // Assume 15 TRY/hour as default labor rate
                            const laborRate = 15;
                            laborCost += (stage.duration / 60) * laborRate;
                        }
                    });

                    // Calculate overhead as 10% of (raw materials + labor)
                    overheadCost = (rawMaterialCost + laborCost) * 0.10;
                }

                // Also check production orders for actual costs
                const productOrders = productionOrders.filter(o =>
                    o.productName === product.name ||
                    o.productId === product.id
                );

                // If we have production orders with actual costs, use those
                if (productOrders.length > 0) {
                    const latestOrder = productOrders.sort((a, b) => {
                        const dateA = a.createdAt?.toDate?.() || new Date(0);
                        const dateB = b.createdAt?.toDate?.() || new Date(0);
                        return dateB - dateA;
                    })[0];

                    if (latestOrder.costs) {
                        rawMaterialCost = latestOrder.costs.rawMaterials || rawMaterialCost;
                        subcontractingCost = latestOrder.costs.subcontracting || subcontractingCost;
                        laborCost = latestOrder.costs.labor || laborCost;
                        overheadCost = latestOrder.costs.overhead || overheadCost;
                    }
                }

                const totalCost = rawMaterialCost + subcontractingCost + laborCost + overheadCost;

                return {
                    id: product.id,
                    name: product.name,
                    code: product.barcode || product.code || '',
                    unitCost: {
                        rawMaterials: parseFloat(rawMaterialCost.toFixed(2)),
                        subcontracting: parseFloat(subcontractingCost.toFixed(2)),
                        labor: parseFloat(laborCost.toFixed(2)),
                        overhead: parseFloat(overheadCost.toFixed(2)),
                        total: parseFloat(totalCost.toFixed(2))
                    },
                    hasRecipe: !!recipe,
                    lastCalculated: recipe?.updatedAt?.toDate?.() || recipe?.createdAt?.toDate?.() || new Date()
                };
            });
    }, [products, recipes, rawMaterials, subcontractors, productionOrders, rawMaterialPurchases]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount);
    };

    const filteredProducts = productsWithCosts.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.code && product.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Calculate totals for summary cards
    const totals = useMemo(() => {
        return filteredProducts.reduce((acc, product) => ({
            rawMaterials: acc.rawMaterials + (product.unitCost.rawMaterials || 0),
            subcontracting: acc.subcontracting + (product.unitCost.subcontracting || 0),
            labor: acc.labor + (product.unitCost.labor || 0),
            overhead: acc.overhead + (product.unitCost.overhead || 0)
        }), { rawMaterials: 0, subcontracting: 0, labor: 0, overhead: 0 });
    }, [filteredProducts]);

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <p>Maliyet verileri yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Maliyet Hesaplama</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>
                        Ürün bazlı maliyet analizleri ve hesaplamalar
                        {rawMaterialPurchases.length > 0 && (
                            <span style={{ marginLeft: '10px', color: '#059669', fontSize: '12px' }}>
                                ✓ {rawMaterialPurchases.length} satın alma kaydından fiyat bilgisi alınıyor
                            </span>
                        )}
                    </p>
                </div>
            </div>

            <div style={styles.box}>
                <h3 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>Maliyet Özeti</h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                    <div style={styles.costCard}>
                        <div style={styles.costCardHeader}>
                            <h4 style={styles.costCardTitle}>Hammadde Maliyeti</h4>
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a' }}>
                            {formatCurrency(totals.rawMaterials)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                            Tüm ürünlerin hammadde maliyeti toplamı (satın alma fiyatlarından)
                        </div>
                    </div>

                    <div style={styles.costCard}>
                        <div style={styles.costCardHeader}>
                            <h4 style={styles.costCardTitle}>Fason Maliyeti</h4>
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a' }}>
                            {formatCurrency(totals.subcontracting)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                            Tüm ürünlerin fason maliyeti toplamı
                        </div>
                    </div>

                    <div style={styles.costCard}>
                        <div style={styles.costCardHeader}>
                            <h4 style={styles.costCardTitle}>İşçilik Maliyeti</h4>
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a' }}>
                            {formatCurrency(totals.labor)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                            Tüm ürünlerin işçilik maliyeti toplamı
                        </div>
                    </div>

                    <div style={styles.costCard}>
                        <div style={styles.costCardHeader}>
                            <h4 style={styles.costCardTitle}>Genel Giderler</h4>
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a' }}>
                            {formatCurrency(totals.overhead)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                            Tüm ürünlerin genel gider payı toplamı
                        </div>
                    </div>
                </div>
            </div>

            <div style={styles.box}>
                <div style={styles.searchContainer}>
                    <div style={styles.searchInputContainer}>
                        <span style={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder="Ürün adı veya kodu ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>

                {filteredProducts.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>💰</div>
                        <h3 style={styles.emptyText}>Ürün Bulunamadı</h3>
                        <p style={styles.emptySubtext}>
                            {searchTerm ? 'Arama kriterlerinize uygun ürün bulunamadı.' : 'Henüz hiç ürün eklenmemiş veya reçete tanımlanmamış.'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() => navigate('/stok')}
                                style={{
                                    marginTop: '20px',
                                    padding: '12px 24px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
                                    color: 'white',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}
                            >
                                Ürünlere Git
                            </button>
                        )}
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Ürün</th>
                                    <th style={styles.th}>Kod</th>
                                    <th style={styles.th}>Hammadde</th>
                                    <th style={styles.th}>Fason</th>
                                    <th style={styles.th}>İşçilik</th>
                                    <th style={styles.th}>Genel Gider</th>
                                    <th style={styles.th}>Toplam</th>
                                    <th style={styles.th}>Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map(product => (
                                    <tr
                                        key={product.id}
                                        style={{ ...styles.trHover, ...(hoveredRow === product.id ? styles.trHoverActive : {}) }}
                                        onMouseEnter={() => setHoveredRow(product.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        onClick={() => navigate(`/uretim/maliyet-hesaplama/${product.id}`)}
                                    >
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '600' }}>{product.name}</div>
                                        </td>
                                        <td style={styles.td}>{product.code || '-'}</td>
                                        <td style={styles.td}>{formatCurrency(product.unitCost.rawMaterials)}</td>
                                        <td style={styles.td}>{formatCurrency(product.unitCost.subcontracting)}</td>
                                        <td style={styles.td}>{formatCurrency(product.unitCost.labor)}</td>
                                        <td style={styles.td}>{formatCurrency(product.unitCost.overhead)}</td>
                                        <td style={{ ...styles.td, fontWeight: '700', color: '#0f172a' }}>
                                            {formatCurrency(product.unitCost.total)}
                                        </td>
                                        <td style={styles.td}>
                                            {product.hasRecipe ? (
                                                <span style={{
                                                    background: '#dcfce7',
                                                    color: '#166534',
                                                    padding: '4px 8px',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: '500'
                                                }}>
                                                    Reçeteli
                                                </span>
                                            ) : (
                                                <span style={{
                                                    background: '#fef3c7',
                                                    color: '#92400e',
                                                    padding: '4px 8px',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: '500'
                                                }}>
                                                    Reçete Yok
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CostCalculation;